import { useState, useEffect, useCallback, useMemo } from 'react';
import { ethers } from 'ethers';
import { CURRENT_NETWORK } from '../utils/constants';

// 创建默认的只读 Provider（用于未连接钱包时读取链上数据）
const createDefaultProvider = () => {
  // 使用多个 RPC 节点，提高可靠性
  const rpcUrls = CURRENT_NETWORK.rpcUrls;
  // 随机选择一个 RPC 节点，避免单点故障
  const randomRpc = rpcUrls[Math.floor(Math.random() * rpcUrls.length)];
  return new ethers.JsonRpcProvider(randomRpc);
};

export function useWallet() {
  const [account, setAccount] = useState(null);
  const [chainId, setChainId] = useState(null);
  const [walletProvider, setWalletProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [isConnecting, setIsConnecting] = useState(false);
  const [error, setError] = useState(null);

  // 默认只读 Provider（始终可用）
  const defaultProvider = useMemo(() => createDefaultProvider(), []);

  // 优先使用钱包 Provider，否则使用默认 Provider
  const provider = walletProvider || defaultProvider;

  const isCorrectNetwork = chainId === parseInt(CURRENT_NETWORK.chainId, 16);

  // 检查钱包是否已连接
  const checkConnection = useCallback(async () => {
    if (typeof window.ethereum === 'undefined') return;

    try {
      const accounts = await window.ethereum.request({ method: 'eth_accounts' });
      if (accounts.length > 0) {
        const browserProvider = new ethers.BrowserProvider(window.ethereum);
        const walletSigner = await browserProvider.getSigner();
        const network = await browserProvider.getNetwork();

        setAccount(accounts[0]);
        setChainId(Number(network.chainId));
        setWalletProvider(browserProvider);
        setSigner(walletSigner);
      }
    } catch (err) {
      console.error('Check connection error:', err);
    }
  }, []);

  // 连接钱包
  const connect = useCallback(async () => {
    if (typeof window.ethereum === 'undefined') {
      setError('请安装 MetaMask 钱包');
      return;
    }

    setIsConnecting(true);
    setError(null);

    try {
      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      const browserProvider = new ethers.BrowserProvider(window.ethereum);
      const walletSigner = await browserProvider.getSigner();
      const network = await browserProvider.getNetwork();

      setAccount(accounts[0]);
      setChainId(Number(network.chainId));
      setWalletProvider(browserProvider);
      setSigner(walletSigner);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsConnecting(false);
    }
  }, []);

  // 断开连接
  const disconnect = useCallback(() => {
    setAccount(null);
    setChainId(null);
    setWalletProvider(null);
    setSigner(null);
  }, []);

  // 切换网络
  const switchNetwork = useCallback(async () => {
    if (typeof window.ethereum === 'undefined') return;

    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: CURRENT_NETWORK.chainId }],
      });
    } catch (switchError) {
      // 如果网络不存在，添加网络
      if (switchError.code === 4902) {
        try {
          await window.ethereum.request({
            method: 'wallet_addEthereumChain',
            params: [CURRENT_NETWORK],
          });
        } catch (addError) {
          setError('添加网络失败');
        }
      } else {
        setError('切换网络失败');
      }
    }
  }, []);

  // 监听账户和网络变化
  useEffect(() => {
    if (typeof window.ethereum === 'undefined') return;

    const handleAccountsChanged = (accounts) => {
      if (accounts.length === 0) {
        disconnect();
      } else {
        setAccount(accounts[0]);
      }
    };

    const handleChainChanged = (chainId) => {
      setChainId(parseInt(chainId, 16));
      window.location.reload();
    };

    window.ethereum.on('accountsChanged', handleAccountsChanged);
    window.ethereum.on('chainChanged', handleChainChanged);

    checkConnection();

    return () => {
      window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      window.ethereum.removeListener('chainChanged', handleChainChanged);
    };
  }, [checkConnection, disconnect]);

  return {
    account,
    chainId,
    provider,
    signer,
    isConnecting,
    isConnected: !!account,
    isCorrectNetwork,
    error,
    connect,
    disconnect,
    switchNetwork,
  };
}
